import React from "react";
import { observer } from "mobx-react";
import { Box, HStack, Image, Link, Stack, Text } from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { renderInnerHtml } from "@/helpers/dom";
import { AppLink } from "@/routing/AppLink";
import { PageContainer } from "./PageContainer";
import { MailToLink } from "../Link/MailToLink";
import { LegitScriptSeal } from "./LegitScriptSeal";
import { useRouterState } from "@tanstack/react-router";

export const Footer = observer(() => {
  const { activeLanguage, activeIndigenousCommunity, showLeavingModal } =
    useStore();
  const language = translations.common["footer-section"][activeLanguage];
  const indigenousLanguage = translations.common.indigenous[activeLanguage];
  const { location } = useRouterState();

  const footerData =
    activeIndigenousCommunity || location.pathname === "/indigenous"
      ? {
          approvalCode: language.approval_code,
          email: indigenousLanguage.email,
          emailSubject: indigenousLanguage.email_subject,
        }
      : {
          approvalCode: language.approval_code,
          email: language.white_banner_email,
          emailSubject: undefined,
        };

  return (
    <Box
      as="footer"
      color="white"
    >
      <Box
        py="6"
        color="black"
        bg="white"
      >
        <PageContainer
          display="flex"
          justifyContent="center"
        >
          <Text
            maxWidth="container.md"
            textAlign="center"
          >
            {renderInnerHtml(language.white_banner_text)}
            <Text
              as="span"
              color="primary"
              whiteSpace="nowrap"
            >
              {renderInnerHtml(language.white_banner_phone)}
            </Text>
            {renderInnerHtml(language.or)}
            <MailToLink
              color="primary"
              href={footerData.email}
              subject={footerData.emailSubject}
            >
              {renderInnerHtml(footerData.email)}
            </MailToLink>
            .
          </Text>
        </PageContainer>
      </Box>

      <Box
        py="8"
        bg="bg"
      >
        <PageContainer>
          <Stack spacing="6">
            <HStack
              fontSize="xs"
              textDecoration="underline"
              spacing="4"
            >
              <Link
                href="https://www.emergentbiosolutions.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {language.privacy_policy}
              </Link>
              <Box>
                <Link
                  as={AppLink}
                  to="/general-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {language.terms_condition}
                </Link>
              </Box>
            </HStack>

            <HStack
              justifyContent="space-between"
              flexDirection={{ sm: "column", md: "row" }}
            />

            <HStack
              alignItems={{ base: "center", md: "flex-end" }}
              flexDirection={{ base: "column", md: "row" }}
              justifyContent="space-between"
              gap={{ base: "10", md: "0" }}
            >
              <Stack
                fontSize="xs"
                maxW="container.md"
                spacing="6"
              >
                <Image
                  src={language.emergent_logo}
                  width="203px"
                  height="97px"
                  alt="Narcan Spray Logo"
                />
                <Stack>
                  <Text mb="2">Use as directed.</Text>
                  <Text fontWeight="bold">
                    {renderInnerHtml(language.distributed)}
                  </Text>
                  <Text>{renderInnerHtml(language.copyright)}</Text>
                  <Text>{renderInnerHtml(language.register_narcan)}</Text>
                  <Text>{renderInnerHtml(language.register_emergent)}</Text>
                </Stack>
              </Stack>
              <Stack
                spacing="6"
                alignItems="center"
              >
                <HStack
                  spacing="6"
                  alignItems="center"
                >
                  <a
                    href="https://www.emergentbiosolutions.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src="/images/emergent-white-words.png"
                      width="177px"
                      minWidth="177px"
                      alt="Emergent Logo"
                    />
                  </a>

                  <Image
                    src="/images/check-mark.png"
                    height="41px"
                    cursor="pointer"
                    alt="Ad Standards Clearance Services Check Mark - multi-coloured check mark in a box"
                    onClick={() =>
                      showLeavingModal(
                        "https://adstandards.ca/preclearance/infologo/ ",
                      )
                    }
                  />
                </HStack>
                <LegitScriptSeal />
                <Text fontSize="12px">
                  {renderInnerHtml(footerData.approvalCode)}
                </Text>
              </Stack>
            </HStack>
          </Stack>
        </PageContainer>
      </Box>
    </Box>
  );
});
